// ResponsiveDrawer2.tsx
import React, { useState } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  MoveToInbox as InboxIcon,
  Mail as MailIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { BalanceWidget } from "../custom/balance/BalanceWidget";

const drawerWidth = 240;

// Расширяем интерфейс для поддержки вложенности
export interface NavItem {
  label: string;
  route: string;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  children?: NavItem[]; // Вложенные пункты
}

interface ResponsiveDrawerProps {
  window?: () => Window;
  items: NavItem[];
  title?: string;
  note?: string;
  children: React.ReactNode;
}

// Вспомогательный компонент для рендеринга одного пункта меню (рекурсивный)
const NavMenuItem: React.FC<{ 
  item: NavItem; 
  onItemClick: (route: string) => void;
  depth?: number; 
}> = ({ item, onItemClick, depth = 0 }) => {
  const [open, setOpen] = useState(item.defaultOpen ?? false);
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setOpen(!open);
    } else {
      onItemClick(item.route);
    }
  };

  return (
    <>
      <ListItem disablePadding sx={{ display: "block" }}>
        <ListItemButton 
          onClick={handleClick} 
          sx={{ pl: depth * 2 + 2 }} // Смещение для вложенности
        >
          <ListItemIcon>
            {item.icon ?? (depth % 2 === 0 ? <InboxIcon /> : <MailIcon />)}
          </ListItemIcon>
          <ListItemText primary={item.label} />
          {hasChildren ? (open ? <ExpandLess /> : <ExpandMore />) : null}
        </ListItemButton>
      </ListItem>
      
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children!.map((child) => (
              <NavMenuItem 
                key={child.route} 
                item={child} 
                onItemClick={onItemClick} 
                depth={depth + 1} 
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const ResponsiveDrawer: React.FC<ResponsiveDrawerProps> = ({
  window,
  items,
  title = "App",
  note,
  children,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleNavItemClick = (route: string) => {
    navigate(route);
    setMobileOpen(false); // Закрываем мобильный Drawer после клика
  };

  const drawer = (
    <div>
      <Toolbar />
      <Divider />
      <List>
        {items.map((item) => (
          <NavMenuItem 
            key={item.route} 
            item={item} 
            onItemClick={handleNavItemClick} 
          />
        ))}
      </List>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <BalanceWidget note={note} />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
          slotProps={{ root: { keepMounted: true } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};